import React, { useEffect, useRef, useState, memo } from 'react';
import { getDrawnOutputKeys } from '../../pages/Strategies/Modern/StrategyBuilder/config/nodeVisualConfig';
import { createChart, ColorType, CrosshairMode, LineSeries, HistogramSeries, AreaSeries } from 'lightweight-charts';
import { sanitizeChartData, anchorRange, rebaseRange, isSameRange, PRICE_SCALE_WIDTH, PANE_HEIGHT, PANE_STRIP_HEIGHT } from './utils';

//--> 142-42: smallest a sub-pane shrinks to (plot still legible); below that the pane stack scrolls
const MIN_PANE_HEIGHT = 64;

//! 50-11: stable default — the strip value state re-renders this pane, and a fresh `{}` per render would
//*   re-run the data effect (setData) and overwrite live update() points with the stale `data` prop.
const EMPTY_STYLE_OPTIONS = {};

/**
 * IndicatorPane Component
 * Renders an indicator in a separate chart instance below the main chart.
 * Optimized for Line, Area, and Histogram types.
 * 
 * @param {Object} props
 * @param {string} props.id - Unique ID for this pane
 * @param {string} props.type - Indicator type ('RSI', 'MACD', etc.)
 * @param {Array} props.data - Indicator data series
 * @param {string} props.color - Primary color
 * @param {number} props.height - Pane height (default: PANE_HEIGHT)
 * @param {boolean} props.isDarkMode - Theme toggle
 * @param {Function} props.onCrosshairMove - Callback for sync
 * @param {Object} props.externalCrosshair - Position from other charts
 * @param {boolean} props.compact - Strip mode: fixed PANE_STRIP_HEIGHT row, label left, latest value right
 * @param {Function} props.onActivate - Strip activation (click / Enter / Space) while compact
 */
const IndicatorPane = ({
    id,
    type,
    data,
    mainChartData,
    color = '#2962FF',
    height = PANE_HEIGHT,
    isDarkMode = false,
    onCrosshairMove,
    onSeriesCreated, // New: Register series back to parent
    externalCrosshair,
    // syncedLogicalRange, onRangeChange -> Removed in favor of syncService
    syncService, // New Prop
    styleOptions = EMPTY_STYLE_OPTIONS, // New Prop: Full style control
    tickMarkFormatter: externalTickMarkFormatter, // Exchange TZ tick formatter from parent
    timeFormatter: externalTimeFormatter, // Exchange TZ crosshair formatter from parent
    compact = false, // 50-11: collapsed-card strip mode (applyOptions + resize only, never a re-create)
    onActivate,
}) => {
    const chartContainerRef = useRef();
    //* 142-37: pane legend lists each drawn output of a multi-output indicator (labelled, coloured)
    const legendKeys = (() => {
        const sample = (data || []).find(d => d?.value && typeof d.value === 'object')?.value;
        return sample ? getDrawnOutputKeys(Object.keys(sample)) : [];
    })();
    const chartInstanceRef = useRef(null);
    const seriesRef = useRef(null);
    const hasInitialZoomRef = useRef(false); // Track if we've auto-fitted once
    const hasDataRef = useRef(false); // Track whether series data has been loaded
    //! last synced range applied here — its echo (fired on the NEXT draw frame) is not re-broadcast (utils.js isSameRange)
    const appliedSyncRangeRef = useRef(null);
    // Muted until the first incoming range: an unsynced pane must never drag the stack to 1980
    const isMutedRef = useRef(true);
    const syncServiceRef = useRef(syncService);
    syncServiceRef.current = syncService;
    //* first bar of the data handed to this pane's series — stamps emitted ranges (utils.js anchorRange)
    const syncAnchorTimeRef = useRef(null);
    //--> 50-11: deregister this pane's series from the parent registry on unmount (IndicatorOverlay.jsx:57-62 pattern)
    const onSeriesCreatedRef = useRef(onSeriesCreated);
    onSeriesCreatedRef.current = onSeriesCreated;
    //--> 50-11: strip latest value = the primary series' own last value, bound only while compact
    const [latestValue, setLatestValue] = useState(null);
    const compactRef = useRef(compact);
    const boundSeriesRef = useRef(null);
    const readLatestRef = useRef(() => {
        const r = boundSeriesRef.current?.lastValueData(true);
        setLatestValue(r && !r.noData && Number.isFinite(r.price) ? r.price : null);
    });
    const unbindLatest = () => {
        boundSeriesRef.current?.unsubscribeDataChanged?.(readLatestRef.current);
        boundSeriesRef.current = null;
    };
    const bindLatest = (series) => {
        if (!compactRef.current || !series || series === boundSeriesRef.current) return;
        unbindLatest();
        boundSeriesRef.current = series;
        series.subscribeDataChanged(readLatestRef.current);
        readLatestRef.current();
    };
    const primaryKey = legendKeys[0] || 'main';
    const primaryKeyForBind = () => seriesRef.current?.[primaryKey];

    // 1. Initialize Chart
    useEffect(() => {
        if (!chartContainerRef.current) return;

        // Cleanup previous instance
        if (chartInstanceRef.current) {
            chartInstanceRef.current.remove();
            chartInstanceRef.current = null;
        }

        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: isDarkMode ? '#a3a3a3' : '#3c4350',
                attributionLogo: false, // Hide TradingView logo
            },
            grid: {
                vertLines: { color: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)' },
                horzLines: { color: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)' },
            },
            width: chartContainerRef.current.clientWidth,
            //* 142-42: the height the pane actually got (it may shrink to fit the card); `height` is the preferred size.
            height: chartContainerRef.current.clientHeight || height,
            //* 50-11: a strip is a tap target, not a pan surface; rebuilt strips stay non-draggable (critic F6)
            handleScroll: !compactRef.current,
            handleScale: !compactRef.current,
            timeScale: {
                //! 50-11: the main chart owns the time axis; a pane never draws one
                visible: false,
                timeVisible: true,
                secondsVisible: false,
                borderColor: isDarkMode ? '#333333' : '#E6E6E6',
                rightOffset: 12,
                barSpacing: 6,
                fixLeftEdge: false,
                lockVisibleTimeRangeOnResize: true,
                rightBarStaysOnScroll: true,
                ...(externalTickMarkFormatter && {
                    tickMarkFormatter: (time, tickMarkType) => externalTickMarkFormatter(time, tickMarkType),
                }),
            },
            ...(externalTimeFormatter && {
                localization: {
                    timeFormatter: (time) => externalTimeFormatter(time),
                },
            }),
            rightPriceScale: {
                borderColor: isDarkMode ? '#333333' : '#E6E6E6',
                scaleMargins: {
                    top: 0.1,
                    bottom: 0.1,
                },
                minimumWidth: PRICE_SCALE_WIDTH, // Sync with SmartChart for vertical alignment
            },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: {
                    visible: true,
                    style: 2, // LineStyle.Dashed
                    labelVisible: true,
                    color: isDarkMode ? '#FFFFFF' : '#333333', // Explicit contrast
                },
                horzLine: {
                    visible: true,
                    labelVisible: true,
                }
            }
        });

        // Initialize empty series container for dynamic creation
        seriesRef.current = {};
        chartInstanceRef.current = chart;

        //--> Every chart in the stack is a zoom/pan PEER: a drag on the plot lands here — broadcast
        //*   it exactly as the main chart does (SmartChart.jsx subscribeVisibleLogicalRangeChange);
        //*   logical indices are 1:1 across panes, every receiver already ignores a range it is
        //*   already showing.
        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
            if (!range || isMutedRef.current || isSameRange(range, appliedSyncRangeRef.current)) return;
            appliedSyncRangeRef.current = null;
            syncServiceRef.current?.emitRange(anchorRange(range, chart.timeScale(), syncAnchorTimeRef.current));
        });

        // Resize Observer
        const resizeObserver = new ResizeObserver(entries => {
            if (!chartInstanceRef.current) return;
            for (const entry of entries) {
                //* 142-42: follow BOTH dimensions — a fixed chart height inside a shrunk pane overflowed it
                const { width, height: boxHeight } = entry.contentRect;
                if (width > 0 && boxHeight > 0) {
                    chartInstanceRef.current.applyOptions({ width, height: boxHeight });
                } else if (width > 0) {
                    chartInstanceRef.current.applyOptions({ width });
                }
            }
        });
        resizeObserver.observe(chartContainerRef.current);

        // Crosshair Handler
        if (onCrosshairMove) {
            chart.subscribeCrosshairMove((param) => {
                if (param.time) {
                    onCrosshairMove({
                        time: param.time,
                        point: param.point,
                        seriesData: param.seriesData, // Pass full series data for O(1) enrichment
                        sourceId: id
                    });
                } else {
                    onCrosshairMove(null);
                }
            });
        }

        return () => {
             resizeObserver.disconnect();
             unbindLatest();
             //* 50-11: tell the parent registry this pane's series are gone (strip cap unmounts panes)
             onSeriesCreatedRef.current?.(id, null);
             if (chartInstanceRef.current) {
                 chartInstanceRef.current.remove();
                 chartInstanceRef.current = null;
             }
        };
    }, [isDarkMode, type, height, id]); // Re-init on type/theme change (BUT NOT COLOR)

    //--> 50-11: compact <-> full is applyOptions + (re)bind only; the init effect never depends on `compact`
    useEffect(() => {
        compactRef.current = compact;
        chartInstanceRef.current?.applyOptions({ handleScroll: !compact, handleScale: !compact });
        if (compact) bindLatest(primaryKeyForBind());
        else { unbindLatest(); setLatestValue(null); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compact]);


    // ----------------------------------------------------
    // SYNC: TimeScale (Zoom/Pan via Service)
    // ----------------------------------------------------
    
    //--> Receive logical range from syncService (Master = main chart).
    //--> Logical range preserves whitespace beyond data (pan fidelity).
    //--> SyncedIndicatorWrapper pads indicator data to match main chart 1:1,
    //--> so logical indices are aligned across all charts.
    useEffect(() => {
        if (!syncService) return;

        const unsubscribe = syncService.subscribeRange((incoming) => {
            //--> Guard: setVisibleLogicalRange throws if called before series data loads.
            //--> The data-update effect re-syncs via lastRange once data arrives.
            //* Rebased onto THIS pane's bar indices; null while the pane's data is a generation behind the
            //* sender's (older page not rendered here yet) — the data effect re-syncs once it lands.
            const range = hasDataRef.current ? rebaseRange(incoming, chartInstanceRef.current?.timeScale()) : null;
            if (chartInstanceRef.current && range && hasDataRef.current) {
                const current = chartInstanceRef.current.timeScale().getVisibleLogicalRange();
                //* Same tolerance as the main chart's receiver (SmartChart.jsx): a half-bar dead band made the
                //* panes skip small drag deltas (jitter, 'behind the cursor') and settle up to half a bar off.
                if (current && Math.abs(current.from - range.from) < 0.01 && Math.abs(current.to - range.to) < 0.01) {
                    return;
                }

                if (isMutedRef.current) isMutedRef.current = false;
                appliedSyncRangeRef.current = range;
                chartInstanceRef.current.timeScale().setVisibleLogicalRange(range);
            }
        });
        return () => unsubscribe();
    }, [syncService]);


    // 2. Data Update & Series Management
    useEffect(() => {
        if (!chartInstanceRef.current || !data || data.length === 0) return;

        // A. Detect Data Structure (Scalar vs Multi-Value)
        const sample = data.find(d => d.value && typeof d.value === 'object')?.value ?? data.find(d => d.value !== undefined)?.value;
        const isMultiValue = typeof sample === 'object' && sample !== null;
        
        // Helper to determine Series Class based on type
        const getSeriesClassAndOptions = (forcedType, baseOptions, key = '') => {
            const visualType = (forcedType || styleOptions.chartType || 'line').toLowerCase();
            const k = (key || '').toLowerCase();

            // HEURISTIC: MACD lines should always be Lines even if indicator is 'histogram' type
            if (isMultiValue && (k.includes('macd') || k.includes('signal')) && !k.includes('hist')) {
                return { Class: LineSeries, options: baseOptions };
            }

            if (visualType === 'area' || visualType === 'band' || visualType === 'dual_area') {
                const areaColor = baseOptions.color || '#2962FF';
                return {
                    Class: AreaSeries,
                    options: {
                        ...baseOptions,
                        lineColor: areaColor,
                        topColor: styleOptions.topColor || styleOptions.fillColor || `${areaColor}40`, 
                        bottomColor: styleOptions.bottomColor || `${areaColor}00`, 
                    }
                };
            } else if (visualType === 'histogram' || k.includes('hist')) {
                return {
                    Class: HistogramSeries,
                    options: {
                        ...baseOptions,
                        color: baseOptions.color // Can be overridden per-bar
                    }
                };
            }
            // Default Line
            return {
                Class: LineSeries,
                options: baseOptions
            };
        };

        
        const updateSeries = () => {
             // Helper specific for MACD/Signal/Hist formatting
             // Ideally we pass 'schema' prop, but inference works for now.
             
             if (isMultiValue) {
                 //--> Ignore Chandelier `direction` (rendered by custom overlay) and any
                 //*   leading-underscore meta-keys (e.g. `_carry` — internal compute state
                 //*   shipped on the wire for cache/warmup continuity, not a chart series).
                 const keys = getDrawnOutputKeys(Object.keys(sample)); // 142-37 one rule (nodeVisualConfig)
                 
                 // 1. Create/Update Series for each key
                 keys.forEach((key, index) => {
                     // Determine Series Type & Style
                     let baseOptions = {
                         color: color || '#2962FF',
                         lineWidth: 2,
                     };
                     
                     // 1. Get Series Class and Initial Options
                     const { Class: SeriesClass, options: resolvedOptions } = getSeriesClassAndOptions(null, baseOptions, key);
                     baseOptions = resolvedOptions;

                     // 2. Specialized Overrides (Priority: User outputStyles > User outputColors > Heuristics)
                     const userOutputStyle = styleOptions.outputStyles?.[key];
                     
                     if (userOutputStyle) {
                         const userColor = userOutputStyle.color;
                         if (userColor) {
                             baseOptions.color = userColor;
                             if (SeriesClass === AreaSeries) {
                                 const opacity = userOutputStyle.opacity ?? 40;
                                 const opacityHex = Math.round(opacity * 2.55).toString(16).padStart(2, '0');
                                 
                                 baseOptions.lineColor = userColor;
                                 baseOptions.topColor = `${userColor}${opacityHex}`;
                                 baseOptions.bottomColor = `${userColor}00`;
                             }
                         }
                         // Always apply line properties if present in override
                         if (userOutputStyle.lineWidth !== undefined) baseOptions.lineWidth = userOutputStyle.lineWidth;
                         if (userOutputStyle.lineStyle !== undefined) baseOptions.lineStyle = userOutputStyle.lineStyle;
                         if (userOutputStyle.lineType !== undefined) baseOptions.lineType = userOutputStyle.lineType;
                     } 
                     else if (['dual_area', 'multi_area'].includes((styleOptions.chartType || '').toLowerCase())) {
                         // Priority: User Legacy Override via outputColors
                         if (styleOptions.outputColors && styleOptions.outputColors[key]) {
                             const userColor = styleOptions.outputColors[key];
                             baseOptions.color = userColor; // Ensure color is sync'd
                             if (SeriesClass === AreaSeries) {
                                 baseOptions.lineColor = userColor;
                                 baseOptions.topColor = `${userColor}40`;
                                 baseOptions.bottomColor = `${userColor}00`;
                             }
                         } 
                         // Priority: Specific defaults for Long/Short stops if keys match
                         else if (key.includes('long')) {
                             baseOptions.lineColor = '#00E676'; // Bright Green
                             baseOptions.topColor = '#00E67640'; 
                             baseOptions.bottomColor = '#00E67600';
                         } else if (key.includes('short')) {
                             baseOptions.lineColor = '#FF1744'; // Bright Red
                             baseOptions.topColor = '#FF174440';
                             baseOptions.bottomColor = '#FF174400';
                         }
                     } else {
                         // Default color override check even in non-dual mode
                         if (styleOptions.outputColors && styleOptions.outputColors[key]) {
                            baseOptions.color = styleOptions.outputColors[key];
                            if (SeriesClass === AreaSeries) {
                                baseOptions.lineColor = baseOptions.color;
                                baseOptions.topColor = `${baseOptions.color}40`;
                                baseOptions.bottomColor = `${baseOptions.color}00`;
                            }
                         }
                     }
                     
                     // 3. Robust Type Switching (Clean up if class mismatched)
                     if (seriesRef.current[key] && seriesRef.current[key]._seriesType !== SeriesClass.name) {
                        chartInstanceRef.current.removeSeries(seriesRef.current[key]);
                        seriesRef.current[key] = null;
                     }

                     // 4. Get or Create Series
                     if (!seriesRef.current[key]) {
                         seriesRef.current[key] = chartInstanceRef.current.addSeries(SeriesClass, baseOptions);
                         seriesRef.current[key]._seriesType = SeriesClass.name; // Tag it
                         if (onSeriesCreated) onSeriesCreated(id, seriesRef.current[key], key);
                     }
                     
                     // 5. Prepare Data
                     const seriesData = data.map((d, i) => {
                        if (!d || !d.value || typeof d.value !== 'object') {
                            return { time: d?.time }; 
                        }
                        
                        const val = d.value[key];
                        // Handle Histogram Color
                        if (SeriesClass === HistogramSeries) {
                           const prevVal = i > 0 ? (data[i - 1]?.value?.[key]) : undefined;
                           let barColor = (val || 0) >= 0 ? '#26a69a' : '#ef5350'; // Defaults
                           
                           if (userOutputStyle) {
                               if ((val || 0) >= 0) {
                                   if (prevVal === undefined || val > prevVal) {
                                       barColor = userOutputStyle.colorPosUp || userOutputStyle.color || '#26a69a';
                                   } else {
                                       barColor = userOutputStyle.colorPosDown || userOutputStyle.color || '#b2dfdb';
                                   }
                               } else {
                                   if (prevVal === undefined || val < prevVal) {
                                       barColor = userOutputStyle.colorNegDown || userOutputStyle.color || '#ef5350';
                                   } else {
                                       barColor = userOutputStyle.colorNegUp || userOutputStyle.color || '#ffcdd2';
                                   }
                               }
                           }

                           return { 
                               time: d.time, 
                               value: val !== undefined ? val : undefined, 
                               color: barColor
                           };
                        }
                        return { time: d.time, value: val };
                     });
                     
                     seriesRef.current[key].setData(sanitizeChartData(seriesData));
                     if (index === 0) bindLatest(seriesRef.current[key]); // 50-11: primary = first key = legendKeys[0]
                     
                     // 6. Final Sync
                     seriesRef.current[key].applyOptions(baseOptions);
                 });

                 // 2. Key Cleanup: Remove series for keys that no longer exist
                 Object.keys(seriesRef.current).forEach(existingKey => {
                     if (!keys.includes(existingKey) && existingKey !== 'main') {
                         chartInstanceRef.current.removeSeries(seriesRef.current[existingKey]);
                         delete seriesRef.current[existingKey];
                     }
                 });
             } else {
                 // Scalar Data (Standard)
                 // Determine Series Type
                 // Use styleOptions.chartType if available, else infer
                 const { Class: SeriesClass, options } = getSeriesClassAndOptions(null, {
                     color: color,
                     lineWidth: styleOptions?.lineWidth || 2,
                     lineStyle: styleOptions?.lineStyle !== undefined ? styleOptions.lineStyle : 0, // Solid
                     lineType: styleOptions?.lineType !== undefined ? styleOptions.lineType : 0, // Simple
                 });

                 // Re-create series if type changes (Lightweight charts handles duplicate addSeries by returning new one, 
                 // but we should check if instance class matches. For simplicity, remove if mismatched or just trust logic)
                 // Ideally we check instanceof, but simpler to rely on key='main' and assume type stability 
                 // OR remove if type changed. 
                 // Strategy: If existing series exists, check if we need to replace it (e.g. Line -> Area)
                 // API doesn't expose type easily. simplest: Re-create if needed.
                 
                 // TODO: Efficiently switch types. For now, assuming seriesRef.current['main'] matches current need 
                 // might be wrong if user switches type. We should track type.
                 
                 // Force cleanup if we suspect type change (simplest robust way)
                 // We can store _type on the series object
                 if (seriesRef.current['main'] && seriesRef.current['main']._seriesType !== SeriesClass.name) {
                     chartInstanceRef.current.removeSeries(seriesRef.current['main']);
                     seriesRef.current['main'] = null;
                 }


                 if (!seriesRef.current['main']) {
                     seriesRef.current['main'] = chartInstanceRef.current.addSeries(SeriesClass, options);
                     seriesRef.current['main']._seriesType = SeriesClass.name; // Tag it
                     
                     // Add RSI Levels if type is RSI
                     if (type === 'RSI') {
                         seriesRef.current['main'].createPriceLine({ price: 70, color: '#FF5252', lineStyle: 2, axisLabelVisible: false });
                         seriesRef.current['main'].createPriceLine({ price: 30, color: '#4CAF50', lineStyle: 2, axisLabelVisible: false });
                     }
                 }
                 seriesRef.current['main'].setData(sanitizeChartData(data));
                 bindLatest(seriesRef.current['main']); // 50-11
                 
                 // Apply Label Position (Price Scale) if changed
                 if (styleOptions?.labelPosition) {
                     options.priceScaleId = styleOptions.labelPosition === 'left' ? 'left' : 'right';
                 }

                 seriesRef.current['main'].applyOptions(options);
             }
             
             // INJECT BASELINE SERIES TO PREVENT LEADING WHITESPACE PRUNING
             // This absolutely guarantees that the timescale indices perfectly match the main chart
             if (mainChartData && mainChartData.length > 0) {
                 if (!seriesRef.current['_baseline_']) {
                     seriesRef.current['_baseline_'] = chartInstanceRef.current.addSeries(LineSeries, {
                         lineWidth: 0,
                         priceLineVisible: false,
                         lastValueVisible: false,
                         crosshairMarkerVisible: false,
                         autoscaleInfoProvider: () => null,
                         visible: false
                     });
                     seriesRef.current['_baseline_']._seriesType = 'LineSeries';
                 }
                 const baselineData = mainChartData.map(d => ({ 
                     time: d.time, 
                     value: 0 
                 }));
                 seriesRef.current['_baseline_'].setData(baselineData);
             }
        };

        let trySyncInterval;
        try {
            syncAnchorTimeRef.current = data[0]?.time ?? null;
            updateSeries();
            hasDataRef.current = true;
            console.log(`[SYNC-DEBUG] IndicatorPane[${id}] data updated. data.length=${data?.length}, syncService.lastRange=`, syncService?.lastRange);
            if (!hasInitialZoomRef.current) {
                hasInitialZoomRef.current = true;
            }

            //--> After data update, re-apply last known logical range from main chart.
            //--> Polling used because lightweight charts layout and setData are asynchronous.
            if (syncService && chartInstanceRef.current) {
                let attempts = 0;
                trySyncInterval = setInterval(() => {
                    if (!chartInstanceRef.current || !syncService.lastRange) return;
                    try {
                        const current = chartInstanceRef.current.timeScale().getVisibleLogicalRange();
                        const target = rebaseRange(syncService.lastRange, chartInstanceRef.current.timeScale());
                        // Avoid unnecessary updates if already closely aligned
                        if (target && current && Math.abs(current.from - target.from) < 0.1 && Math.abs(current.to - target.to) < 0.1) {
                            clearInterval(trySyncInterval); 
                            return;
                        }
                        //* no target: the last range was measured on data this pane does not hold yet — retry
                        if (target) {
                            appliedSyncRangeRef.current = target;
                            chartInstanceRef.current.timeScale().setVisibleLogicalRange(target);
                        }
                    } catch (e) {
                        console.warn(`[SYNC-DEBUG] IndicatorPane[${id}] re-sync failed:`, e.message);
                    }
                    attempts++;
                    if (attempts >= 10) clearInterval(trySyncInterval); // Try for 1 second max
                }, 100);
            }
        } catch (err) {
            console.error(`[IndicatorPane:${type}] Data error:`, err);
        }

        return () => {
            if (trySyncInterval) clearInterval(trySyncInterval);
        };
    }, [data, type, color, styleOptions, syncService, mainChartData]);


    // 3. Crosshair Sync
    // 3. Crosshair Sync (Via Service)
    useEffect(() => {
        if (!syncService) return;

        // Sub to service events
        const unsubscribe = syncService.subscribe((param) => {
             // If event exists AND it didn't originate from this pane (id)
             if (param && chartInstanceRef.current && param.sourceId !== id) {
                 try {
                     // Direct API call - fast, no re-render
                     const targetSeries = Object.values(seriesRef.current || {})[0];
                     if (targetSeries) {
                         chartInstanceRef.current.setCrosshairPosition(0, param.time, targetSeries);
                     }
                 } catch (e) {}
             } else if (!param && chartInstanceRef.current) {
                 chartInstanceRef.current.clearCrosshairPosition();
             }
        });

        return () => unsubscribe();
    }, [syncService, id]);

    // Handle ExternalCrosshair (still needed if syncing from other SmartCharts not sharing service?)
    // In this context, we assume all panes share the service from SmartChart.
    // If externalCrosshair is passed (e.g. from parent prop), we can still respect it, 
    // but the service is the primary "fast path".
    useEffect(() => {
        if (externalCrosshair && chartInstanceRef.current && externalCrosshair.sourceId !== id) {
             try {
                 const targetSeries = Object.values(seriesRef.current || {})[0];
                 if (targetSeries) {
                     chartInstanceRef.current.setCrosshairPosition(0, externalCrosshair.time, targetSeries);
                 }
             } catch(e) {}
        }
    }, [externalCrosshair, id]);

    const valueColor = styleOptions.outputStyles?.[primaryKey]?.color ?? color;
    const isStrip = compact;
    const stripActivatable = isStrip && !!onActivate;
    const handleStripKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
        }
    };

    return (
        //* 142-42: preferred `height`, may shrink (to MIN_PANE_HEIGHT plot) when the card is short;
        //*   below that the pane stack scrolls (SmartChart). 50-11: compact = fixed strip row.
        <div
            className={`w-full relative flex flex-col min-h-0 border-t border-gray-100 dark:border-gray-800${stripActivatable ? ' cursor-pointer' : ''}`}
            style={isStrip
                ? { flex: `0 0 ${PANE_STRIP_HEIGHT}px`, height: PANE_STRIP_HEIGHT }
                : { flex: `0 1 ${height}px`, minHeight: Math.min(MIN_PANE_HEIGHT, height) }}
            data-testid={`indicator-pane-${id}`}
            {...(stripActivatable && {
                role: 'button',
                tabIndex: 0,
                //* F9 (Verify): the strip's value is part of its accessible name, not hidden behind it
                'aria-label': `${type} ${latestValue === null ? '\u2014' : latestValue.toFixed(2)} \u2014 expand chart`,
                onClick: onActivate,
                onKeyDown: handleStripKeyDown,
            })}
        >
            {/* Legend / Title (strip: single left label, vertically centred) */}
            <div
                className={`absolute left-2 z-10 text-[10px] font-mono text-gray-500 font-bold opacity-70${isStrip ? ' top-1/2 -translate-y-1/2' : ' top-1'}`}
            >
                {type}
                {(isStrip ? legendKeys.slice(0, 1) : legendKeys).map(k => (
                    <span key={k} className="ml-2 font-normal" style={{ color: styleOptions.outputStyles?.[k]?.color }}>
                        {'\u25CF '}{styleOptions.outputLabels?.[k] || k}
                    </span>
                ))}
            </div>
            <div ref={chartContainerRef} className="w-full flex-1 min-h-0" />
            {isStrip && (
                //* 50-11: opaque value cell over the reserved price-scale column (labels stay hidden)
                <div
                    className="absolute top-0 right-0 bottom-0 z-10 flex items-center justify-end pr-2 bg-white dark:bg-bg-2 text-[11px] font-mono font-bold"
                    style={{ width: PRICE_SCALE_WIDTH, color: valueColor }}
                    data-testid={`indicator-strip-value-${id}`}
                >
                    {latestValue === null ? '\u2014' : latestValue.toFixed(2)}
                </div>
            )}
        </div>
    );
};

export default memo(IndicatorPane);
